import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { 
  Building2, Users, User, Globe, ExternalLink, 
  Image, ArrowLeft, Loader2
} from 'lucide-react';
import UnifiedHeader from '../components/UnifiedHeader';
import Footer from '../components/Footer';
import LinkifyText from '../components/LinkifyText';
import { useAuth } from '../context/AuthContext';

const PublicProfilePage = () => {
  const { handle } = useParams();
  const { isAuthenticated } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchProfile();
  }, [handle]);

  const fetchProfile = async () => {
    setLoading(true);
    try {
      const cleanHandle = handle.replace('@', '');
      const res = await fetch(`${API_URL}/api/profile/${cleanHandle}`);
      
      if (!res.ok) {
        if (res.status === 404) {
          setError('Profile not found');
        } else {
          setError('Error loading profile');
        }
        return;
      }

      const data = await res.json();
      setProfile(data);
    } catch (err) {
      setError('Error loading profile');
    } finally {
      setLoading(false);
    }
  };

  const trackClick = async () => {
    if (profile?.affiliate_link) {
      try {
        await fetch(`${API_URL}/api/influencer/track-click/${profile.handle}`);
      } catch (err) {
        console.error('Error tracking click');
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-rose-50 to-orange-50 flex flex-col">
        <UnifiedHeader />
        <div className="pt-20 flex-1 flex items-center justify-center">
          <Loader2 className="w-8 h-8 text-rose-500 animate-spin" />
        </div>
        <Footer isAuthenticated={isAuthenticated} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-rose-50 to-orange-50 flex flex-col">
        <UnifiedHeader />
        <div className="pt-20 flex-1 flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-800 mb-2">{error}</h1>
            <Link to={isAuthenticated ? "/dashboard" : "/"} className="text-rose-500 hover:underline">
              {isAuthenticated ? "Go to Dashboard" : "Go to homepage"}
            </Link>
          </div>
        </div>
        <Footer isAuthenticated={isAuthenticated} />
      </div>
    );
  }

  const TypeIcon = profile.user_type === 'brand' ? Building2 : 
                   profile.user_type === 'influencer' ? Users : User;

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-rose-50 to-orange-50 flex flex-col">
      <UnifiedHeader />

      {/* Main Content with top padding for fixed nav */}
      <div className="pt-20 flex-1">
        <div className="max-w-4xl mx-auto px-4 py-12">
          {/* Profile Header */}
          <div className="bg-white rounded-2xl shadow-lg p-8 mb-8">
          <div className="flex items-start space-x-6">
            <div className="w-24 h-24 bg-gradient-to-r from-rose-500 to-orange-500 rounded-2xl flex items-center justify-center overflow-hidden">
              {profile.profile_image ? (
                <img 
                  src={profile.profile_image.startsWith('data:') ? profile.profile_image : 
                       profile.profile_image.startsWith('http') ? profile.profile_image :
                       `data:image/png;base64,${profile.profile_image}`} 
                  alt={profile.name}
                  className="w-full h-full rounded-2xl object-cover"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              ) : (
                <TypeIcon className="w-12 h-12 text-white" />
              )}
            </div>
            
            <div className="flex-1 min-w-0">
              <div className="flex items-center space-x-3 mb-2">
                <h1 className="text-2xl font-bold text-gray-800 truncate">
                  {profile.company_name || profile.name}
                </h1>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                  profile.user_type === 'brand' ? 'bg-blue-100 text-blue-700' :
                  profile.user_type === 'influencer' ? 'bg-purple-100 text-purple-700' :
                  'bg-rose-100 text-rose-700'
                }`}>
                  {profile.user_type}
                </span>
              </div>
              
              <p className="text-gray-500 mb-3">@{profile.handle}</p>
              
              {profile.bio && (
                <p className="text-gray-600 mb-4 line-clamp-3 break-all overflow-hidden">{profile.bio}</p>
              )}
              
              <div className="flex items-center space-x-4">
                {profile.website && (
                  <a 
                    href={profile.website} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="flex items-center text-gray-500 hover:text-rose-500"
                  >
                    <Globe className="w-4 h-4 mr-1" />
                    Website
                  </a>
                )}
                
                {profile.affiliate_link && profile.user_type === 'influencer' && (
                  <a 
                    href={profile.affiliate_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={trackClick}
                    className="flex items-center px-4 py-2 bg-gradient-to-r from-rose-500 to-orange-500 text-white rounded-lg font-medium hover:shadow-md transition-shadow"
                  >
                    <ExternalLink className="w-4 h-4 mr-2" />
                    Shop My Looks
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Portfolio (for brands) */}
        {profile.user_type === 'brand' && profile.portfolio && profile.portfolio.length > 0 && (
          <div>
            <h2 className="text-xl font-bold text-gray-800 mb-6">Clothing Collection</h2>
            <div className="grid md:grid-cols-3 gap-6">
              {profile.portfolio.map((item) => (
                <div key={item.id} className="bg-white rounded-xl shadow-sm overflow-hidden hover:shadow-md transition-shadow">
                  {(item.image_url || item.cloudinary_url || (item.image_base64 && item.image_base64.length > 100)) ? (
                    <div className="w-full aspect-[3/4] bg-gray-50 flex items-center justify-center">
                      <img 
                        src={item.image_url || item.cloudinary_url || `data:image/png;base64,${item.image_base64}`} 
                        alt={item.name}
                        className="w-full h-full object-contain"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZjNmNGY2Ii8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIxNCIgZmlsbD0iIzljYTNhZiIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPk5vIEltYWdlPC90ZXh0Pjwvc3ZnPg==';
                        }}
                      />
                    </div>
                  ) : (
                    <div className="w-full aspect-[3/4] bg-gray-100 flex items-center justify-center">
                      <Image className="w-12 h-12 text-gray-300" />
                    </div>
                  )}
                  <div className="p-4">
                    <h3 className="font-semibold text-gray-800 truncate">{item.name}</h3>
                    {item.description && (
                      <p className="text-sm text-gray-500 mt-1 line-clamp-2 break-all">
                        <LinkifyText text={item.description} />
                      </p>
                    )}
                    {item.price && (
                      <p className="text-rose-500 font-bold mt-2">${item.price}</p>
                    )}
                    {item.affiliate_link && (
                      <a 
                        href={item.affiliate_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-3 inline-flex items-center text-sm text-rose-500 hover:underline"
                      >
                        Shop Now <ExternalLink className="w-3 h-3 ml-1" />
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Featured Looks (for influencers) */}
        {profile.user_type === 'influencer' && profile.featured_looks && profile.featured_looks.length > 0 && (
          <div>
            <h2 className="text-xl font-bold text-gray-800 mb-6">Featured Looks</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {profile.featured_looks.map((look) => (
                <div key={look.id} className="bg-white rounded-xl shadow-sm overflow-hidden hover:shadow-md transition-shadow">
                  <img 
                    src={look.image_url || (look.image_base64 ? `data:image/png;base64,${look.image_base64}` : '/placeholder.png')} 
                    alt={look.description || 'Look'}
                    className="w-full aspect-[3/4] object-contain bg-gray-50"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZjNmNGY2Ii8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIxNCIgZmlsbD0iIzljYTNhZiIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPk5vIEltYWdlPC90ZXh0Pjwvc3ZnPg==';
                    }}
                  />
                  {look.description && (
                    <div className="p-4">
                      <p className="text-gray-600 line-clamp-3 break-all">
                        <LinkifyText text={look.description} />
                      </p>
                      {look.tags && look.tags.length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-2">
                          {look.tags.map((tag, idx) => (
                            <span key={idx} className="text-xs bg-rose-100 text-rose-500 px-2 py-1 rounded-full">
                              #{tag}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Collaborations Section */}
        {profile.collaborations && profile.collaborations.length > 0 && (
          <div className="mt-10">
            <h2 className="text-xl font-bold text-gray-800 mb-6">
              {profile.user_type === 'brand' ? 'Influencer Collaborations' : 'Brand Collaborations'}
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {profile.collaborations.map((collab) => {
                // Determine the image source - handle both image_url and image_base64
                const imageSrc = collab.image_url ? collab.image_url :
                                 collab.image_base64 ? (collab.image_base64.startsWith('data:') ? collab.image_base64 : `data:image/jpeg;base64,${collab.image_base64}`) :
                                 null;
                
                // Determine if the card should be clickable (has product_url)
                const isClickable = collab.product_url && collab.product_url.trim() !== '';
                const productHref = isClickable && !/^https?:\/\//i.test(collab.product_url)
                  ? `https://${collab.product_url}`
                  : collab.product_url;
                
                const CardContent = (
                  <>
                    <div className="relative">
                      {imageSrc ? (
                        <img 
                          src={imageSrc} 
                          alt={collab.caption || 'Collaboration'}
                          className="w-full aspect-[3/4] object-contain bg-gray-50"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZjNmNGY2Ii8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIxNCIgZmlsbD0iIzljYTNhZiIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPk5vIEltYWdlPC90ZXh0Pjwvc3ZnPg==';
                          }}
                        />
                      ) : (
                        <div className="w-full aspect-[3/4] bg-gray-100 flex items-center justify-center">
                          <Image className="w-12 h-12 text-gray-300" />
                        </div>
                      )}
                      {/* Partner badge */}
                      <div className="absolute top-3 left-3 bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full shadow-sm">
                        {profile.user_type === 'brand' ? (
                          <Link to={`/profile/${collab.influencer_handle}`} className="text-sm font-medium text-gray-700 hover:text-rose-500" onClick={(e) => e.stopPropagation()}>
                            @{collab.influencer_handle}
                          </Link>
                        ) : (
                          <Link to={`/profile/${collab.brand_handle}`} className="text-sm font-medium text-gray-700 hover:text-rose-500" onClick={(e) => e.stopPropagation()}>
                            @{collab.brand_handle}
                          </Link>
                        )}
                      </div>
                      {/* Shop Now badge if clickable */}
                      {isClickable && (
                        <div className="absolute top-3 right-3 bg-rose-500 text-white px-3 py-1 rounded-full shadow-sm text-xs font-medium">
                          Shop Now →
                        </div>
                      )}
                    </div>
                    <div className="p-4">
                      {collab.product_name && (
                        <p className="text-xs text-rose-500 font-medium mb-1">Featuring: {collab.product_name}</p>
                      )}
                      {collab.caption && (
                        <p className="text-gray-600 text-sm">
                          <LinkifyText text={collab.caption} />
                        </p>
                      )}
                    </div>
                  </>
                );
                
                return isClickable ? (
                  <a 
                    key={collab.id} 
                    href={productHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-white rounded-xl shadow-sm overflow-hidden hover:shadow-lg transition-all cursor-pointer group"
                    data-testid={`collab-link-${collab.id}`}
                  >
                    {CardContent}
                  </a>
                ) : (
                  <div key={collab.id} className="bg-white rounded-xl shadow-sm overflow-hidden hover:shadow-md transition-shadow">
                    {CardContent}
                  </div>
                );
              })}
            </div>
          </div>
        )}
        </div>
      </div>

      <Footer isAuthenticated={isAuthenticated} />
    </div>
  );
};

export default PublicProfilePage;
